// Asset registry — Phase 7 scaffold.
//
// Looks for `.glb` files at /models/<series-dir>/<assetKey>.glb at boot. Each
// scene module asks the registry via `get(key)` and receives either a fresh
// clone of the loaded scene graph or `null` — in which case the caller is
// responsible for falling back to its placeholder geometry.
//
// **Zero-code drop-in**: place a `.glb` named after one of the AssetKeys in
// /public/models/series-3/ (or your active series dir) and the next reload
// picks it up automatically.

import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import {
  AssetKeys,
  SeriesConfig,
  DefaultSeriesId,
} from '../config/balance.js'

// Re-export so the boot sequence can fall back to the active default series
// without importing balance directly.
export const DEFAULT_SERIES = DefaultSeriesId

const loader = new GLTFLoader()
// Models are meshopt-compressed (see scripts/optimize-models.mjs) — the decoder
// is required to read them. Harmless for any uncompressed GLBs too.
loader.setMeshoptDecoder(MeshoptDecoder)
const cache = new Map() // assetKey → THREE.Object3D
const status = new Map() // assetKey → 'loaded' | 'missing' | 'error'
const clips = new Map() // assetKey → THREE.AnimationClip[] (rigged models only)

// Some series ship many objects inside a few big "master" GLBs that share one
// texture (e.g. the low-poly Asian set: Exteriors.glb + Tiles.glb). We load
// each master once and index every NAMED node so individual props can be
// pulled out by name via getByName(). Tiles.glb is intentionally NOT loaded —
// its tiles are square dioramas; the hex ground is procedural and we only
// want the Exteriors props on top.
const MASTER_FILES = {
  SERIES_4: ['Exteriors', 'Tiles'],
}
const masterIndex = new Map() // node name → Object3D (kept in the master graph)

let activeSeriesId = DefaultSeriesId

export function setSeries(seriesId) {
  if (!SeriesConfig[seriesId]) return false
  activeSeriesId = seriesId
  return true
}

function urlFor(assetKey) {
  const dir = SeriesConfig[activeSeriesId]?.dir
  if (!dir) return null
  return `/models/${dir}/${assetKey}.glb`
}

async function loadOne(assetKey) {
  const url = urlFor(assetKey)
  if (!url) return null
  try {
    // HEAD-check first — a 404 from loadAsync also rejects but printing the
    // error chain in the console for every missing optional asset is noisy.
    const head = await fetch(url, { method: 'HEAD' })
    if (!head.ok) {
      status.set(assetKey, 'missing')
      return null
    }
    const gltf = await loader.loadAsync(url)
    cache.set(assetKey, gltf.scene)
    if (gltf.animations?.length) clips.set(assetKey, gltf.animations)
    status.set(assetKey, 'loaded')
    return gltf.scene
  } catch (err) {
    status.set(assetKey, 'error')
    console.warn(`[assets] failed to load ${assetKey} from ${url}:`, err.message)
    return null
  }
}

// Load a master GLB and index every named node so getByName() can extract
// individual props later.
async function loadMaster(file) {
  const dir = SeriesConfig[activeSeriesId]?.dir
  if (!dir) return
  const url = `/models/${dir}/${file}.glb`
  try {
    const head = await fetch(url, { method: 'HEAD' })
    if (!head.ok) { status.set(`master:${file}`, 'missing'); return }
    const gltf = await loader.loadAsync(url)
    // Compute every node's world matrix so getByName() can bake the full
    // transform (objects nest under a Z-up→Y-up corrected root; without this
    // an extracted prop loses its parents' rotation and lies on its side).
    gltf.scene.updateMatrixWorld(true)
    let n = 0
    gltf.scene.traverse((o) => {
      if (o.name && !masterIndex.has(o.name)) { masterIndex.set(o.name, o); n++ }
    })
    status.set(`master:${file}`, 'loaded')
    console.log(`[assets] master ${file}: indexed ${n} named objects`)
  } catch (err) {
    status.set(`master:${file}`, 'error')
    console.warn(`[assets] master ${file} failed:`, err.message)
  }
}

// Preload every known asset key for the active series, PLUS any master scenes.
// Resolves once they've all either loaded or been declared missing — the rest
// of the boot proceeds regardless (missing assets get placeholder geometry).
// opts.skipMasters: master scenes to leave out (e.g. 'Tiles' once the Synty kit covers the hex field).
export async function preload(keys = Object.values(AssetKeys), opts = {}) {
  const skip = new Set(opts.skipMasters ?? [])
  const masters = (MASTER_FILES[activeSeriesId] ?? []).filter((m) => !skip.has(m))
  await Promise.allSettled(masters.map(loadMaster))
  await Promise.allSettled(keys.map((k) => loadOne(k)))
}

// Load one master scene on demand (the classic Tiles.glb fallback if the Synty kit can't load).
export const preloadMaster = (name) => loadMaster(name)

// Returns a fresh CLONE of the loaded asset (so multiple instances don't share
// transforms). Null means the asset isn't available — caller falls back.
export function get(assetKey) {
  const obj = cache.get(assetKey)
  if (!obj) return null
  // A plain Object3D.clone() leaves a skinned mesh bound to the ORIGINAL skeleton,
  // so a rigged model can't be animated per-instance. SkeletonUtils.clone rebinds
  // each clone to its own copy of the bones.
  let skinned = false
  obj.traverse((o) => { if (o.isSkinnedMesh) skinned = true })
  return skinned ? cloneSkinned(obj) : obj.clone(true)
}

// Animation clips that shipped with a rigged model (e.g. cat_hero: Idle / Run /
// Jump). Empty array for static models. Clips are shared between clones — each
// instance drives them through its own AnimationMixer.
export function getClips(assetKey) {
  return clips.get(assetKey) ?? []
}

// Pull a named object out of a loaded master scene (e.g. 'Building_3', 'Tree_1',
// 'Lightern_2'). Returns a deep clone with the object's full WORLD transform
// baked in — so it stands upright as authored, not in its raw local frame
// (which loses the parents' Z-up→Y-up rotation). Route through fit() to then
// normalize scale/anchor.
export function getByName(name) {
  const obj = masterIndex.get(name)
  if (!obj) return null
  const clone = obj.clone(true)
  obj.matrixWorld.decompose(clone.position, clone.quaternion, clone.scale)
  return clone
}

// Names of every indexed master object (for tooling / inventory).
export function masterNames() {
  return [...masterIndex.keys()]
}

export function has(assetKey) {
  return cache.has(assetKey)
}

export function getStatus() {
  return Object.fromEntries(status)
}

// Manual registration for non-glb assets (e.g. test fixtures). Phase 7 doesn't
// use this but the API is here for the asset pipeline tools to come.
export function register(assetKey, obj) {
  cache.set(assetKey, obj)
  status.set(assetKey, 'loaded')
}

// Auto-fit a loaded model to a known size, regardless of how it was exported.
// External art packs (e.g. the low-poly Asian set) ship at arbitrary scales —
// this normalizes any model so the scene code doesn't care what units the
// artist used. Wraps the model in a group whose ORIGIN is the model's base
// center, so callers just do `group.position.set(x, baseY, z)`.
//
//   footprint  — target size of the largest horizontal dimension (X or Z), in
//                world/tile units. e.g. a building → ~0.65, a ground tile → 1.0.
//   anchorBase — true: bottom of the model sits at the group origin (y=0);
//                false: vertically centered.
export function fit(model, { footprint = 1, anchorBase = true } = {}) {
  const group = new THREE.Group()
  group.add(model)
  group.updateMatrixWorld(true) // ensure fresh matrices before measuring
  const box = new THREE.Box3().setFromObject(model)
  const size = box.getSize(new THREE.Vector3())
  const maxXZ = Math.max(size.x, size.z) || 1
  model.scale.multiplyScalar(footprint / maxXZ)
  // Re-measure after scaling, then re-center on XZ + anchor the base.
  group.updateMatrixWorld(true)
  const box2 = new THREE.Box3().setFromObject(model)
  const center = box2.getCenter(new THREE.Vector3())
  model.position.x -= center.x
  model.position.z -= center.z
  model.position.y -= anchorBase ? box2.min.y : center.y
  return group
}

export { AssetKeys }
